import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  Choice,
  closeCurrentDialog,
  DataStatus,
  Radio,
  showNotification,
} from 'platform/components';
import {Clickable, Show, Text, VStack} from 'platform/foundation';
import {formatPhoneNumber} from 'platform/locale';
import {match, Pattern} from 'ts-pattern';

import {useRef, useState} from 'react';

import {head, isEmpty, isNil, isNotNil, pick} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetBranchListQuery} from '@dms/api/core';
import {useGetCustomerV2Query} from '@dms/api/customer';
import {
  TelephonyCall,
  TelephonyCallChannel,
  TelephonyCallLine,
  TelephonyCallOptionLine,
  useGetTelephonyCallOptionsQuery,
  useStartTelephonyCallMutation,
} from '@dms/api/settings';
import {GetBranchListApiResponse} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {getCustomerName, handleApiError, usePermissions} from '@dms/shared';

import {ApiError, RequiredTestIdProps, suffixTestId, useNavigate, useOnMount} from 'shared/web';

import {TELEPHONY_CALL_ERROR_CODES} from '../../constants/TELEPHONY_CALL_ERROR_CODES';
import {useApplyTelephonyCallState} from '../../hooks/useApplyTelephonyCallState';
import {useBrowserPhone} from '../../hooks/useBrowserPhone';
import {BrowserPhoneFailure} from '../../types/BrowserPhoneFailure';
import {getCustomerCallPhoneNumbers} from '../../utils/getCustomerCallPhoneNumbers';
import {getTelephonyCallApiErrorCode} from '../../utils/getTelephonyCallApiErrorCode';
import {isForbiddenApiError} from '../../utils/isForbiddenApiError';
import {requestMicrophoneAccess} from '../../utils/requestMicrophoneAccess';

interface CallCustomerDialogProps extends RequiredTestIdProps {
  customerId: string;
}

type CallBlocker =
  | 'NOT_ENABLED'
  | 'NO_USER_PHONE'
  | 'NO_LINE'
  | 'START_FAILED'
  | Exclude<BrowserPhoneFailure, 'CONNECTION_FAILED'>;

const SEPARATOR = ' · ';
/** One radio group, so the arrow keys move between the channel cards. */
const CHANNEL_RADIO_NAME = 'callCustomerChannel';

/** "Branch · Line · number"; a missing branch (unknown, or not loaded) leaves the label shorter. */
const getLineLabel = (
  line: TelephonyCallOptionLine,
  branchList: GetBranchListApiResponse | undefined
) =>
  [
    branchList?.branchListItems.find((branch) => branch.id === line.branchId)?.marketingName,
    line.name,
    formatPhoneNumber(line.phoneNumber),
  ]
    .filter(isNotNilOrEmpty)
    .join(SEPARATOR);

/**
 * Browser is the default when both channels work. A chosen browser that stopped working is never
 * swapped for the mobile: nothing is selected and the caller picks again.
 */
const getCallChannel = (
  isBrowserAvailable: boolean,
  isMobileAvailable: boolean,
  chosenChannel: TelephonyCallChannel | null
) =>
  match<[boolean, boolean], TelephonyCallChannel | null>([isBrowserAvailable, isMobileAvailable])
    .with([true, true], () => chosenChannel ?? 'browser')
    .with([true, false], () => 'browser')
    .with([false, true], () => (chosenChannel === 'browser' ? null : 'mobile'))
    .otherwise(() => 'mobile');

// eslint-disable-next-line max-params -- Pre-existing violation
const buildStartedCall = (
  channel: TelephonyCallChannel,
  callId: string,
  customerId: string,
  customerName: string | null,
  phoneNumber: string,
  line: TelephonyCallLine
): TelephonyCall => ({
  id: callId,
  direction: 'outbound',
  channel,
  // A browser call has no caller leg: it starts at dialling the customer.
  status: channel === 'browser' ? 'connecting_customer' : 'ringing_user',
  outcome: null,
  customerId,
  customerName,
  customerPhoneNumber: phoneNumber,
  line,
  startedAt: new Date().toISOString(),
  answeredAt: null,
  connectedAt: null,
  endedAt: null,
  durationSeconds: null,
  answeredBy: null,
  records: [],
  note: null,
});

/**
 * Confirms an outbound call: which customer number, which line it is made from (preselected with
 * the caller's branch line, always sent) and, on every call, whether the caller talks through their
 * mobile or this browser. A channel is offered only when it can work: mobile needs a number in the
 * caller's profile, browser needs the tab's softphone. Everything else comes from one
 * `GET /telephony/call-options`;
 * after a successful start it seeds the current-call cache (`ringing_user` for mobile,
 * `connecting_customer` for browser) so the top-bar indicator shows up before the first push.
 */
// eslint-disable-next-line complexity -- Pre-existing violation
export function CallCustomerDialog(props: CallCustomerDialogProps) {
  const navigate = useNavigate();
  const applyCallState = useApplyTelephonyCallState();
  const {state: browserPhoneState, phone} = useBrowserPhone();

  const [canWriteTelephonySettings] = usePermissions({
    permissionKeys: ['telephonySettingsWrite'],
  });

  const {
    data: customer,
    isLoading: isCustomerLoading,
    isError: isCustomerError,
  } = useGetCustomerV2Query({customerId: props.customerId});

  const {
    data: callOptions,
    isLoading: areOptionsLoading,
    isFetching: areOptionsFetching,
    error: optionsError,
    refetch: refetchCallOptions,
  } = useGetTelephonyCallOptionsQuery(undefined, {refetchOnMountOrArgChange: true});

  // Licence off or role without TELEPHONY_CALL: a state of the tenant, not a failed request.
  const isTelephonyForbidden = isForbiddenApiError(optionsError);
  const areOptionsError = !!optionsError && !isTelephonyForbidden;

  // Only names the line options; a failed read leaves the branch out of the label.
  const {data: branchList, isLoading: isBranchListLoading} = useGetBranchListQuery();

  const [startCall, {isLoading: isStarting}] = useStartTelephonyCallMutation();

  const [pendingChannel, setPendingChannel] = useState<TelephonyCallChannel | null>(null);
  // The microphone prompt can outlive the dialog; a closed dialog must not place the call.
  const isMountedRef = useRef(false);

  useOnMount(
    () => {
      isMountedRef.current = true;
    },
    () => {
      isMountedRef.current = false;
    }
  );
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState<string | null>(null);
  const [selectedLineId, setSelectedLineId] = useState<string | null>(null);
  const [chosenChannel, setChosenChannel] = useState<TelephonyCallChannel | null>(null);
  const [startFailure, setStartFailure] = useState<CallBlocker | null>(null);

  const phoneOptions = getCustomerCallPhoneNumbers(customer);
  const phoneNumber = selectedPhoneNumber ?? head(phoneOptions)?.value ?? null;

  const branchLine = callOptions?.branchLine ?? null;
  const lines = callOptions?.lines ?? [];
  const lineId = selectedLineId ?? branchLine?.id ?? null;
  const selectedLine = lines.find((line) => line.id === lineId) ?? null;

  const isBrowserAvailable = browserPhoneState.availability === 'available';
  const isMobileAvailable = isNotNil(callOptions?.userPhoneNumber);
  const channel = getCallChannel(isBrowserAvailable, isMobileAvailable, chosenChannel);

  const availabilityBlocker = match<[boolean, boolean, boolean], CallBlocker | null>([
    isTelephonyForbidden,
    isNotNil(callOptions) && !isMobileAvailable && !isBrowserAvailable,
    isNotNil(callOptions) && isNil(branchLine) && isEmpty(lines),
  ])
    .with([true, Pattern.boolean, Pattern.boolean], () => 'NOT_ENABLED')
    .with([false, true, Pattern.boolean], () => 'NO_USER_PHONE')
    .with([false, false, true], () => 'NO_LINE')
    .otherwise(() => null);

  const blocker = startFailure ?? availabilityBlocker;
  const isBlocked =
    blocker === 'NOT_ENABLED' || blocker === 'NO_USER_PHONE' || blocker === 'NO_LINE';
  // From the microphone prompt to the 201 the choices are read: a late change would be ignored.
  const isPending = isNotNil(pendingChannel);
  // While the options reload after a line error, the stale line must not be sent again.
  const isBusy = areOptionsFetching || isStarting || isPending;
  const canSubmit =
    !isBlocked && isNotNil(phoneNumber) && isNotNil(selectedLine) && isNotNil(channel) && !isBusy;

  const handleOpenTelephonySettings = () => {
    closeCurrentDialog();
    navigate(settingsRoutes.telephony);
  };

  const startCallOn = (channel: TelephonyCallChannel) => {
    if (isNil(phoneNumber) || isNil(selectedLine)) {
      setPendingChannel(null);
      return;
    }

    startCall({
      body: {
        customerId: props.customerId,
        phoneNumber,
        lineId: selectedLine.id,
        channel,
      },
    })
      .unwrap()
      .then((response) => {
        // The first push may already be in the cache when the 201 lands; the merge keeps it.
        applyCallState(
          buildStartedCall(
            channel,
            response.id,
            props.customerId,
            getCustomerName(customer) ?? null,
            phoneNumber,
            pick(['id', 'name', 'phoneNumber'], selectedLine)
          )
        );

        if (channel === 'browser') {
          phone.connect(response.id);
        }

        closeCurrentDialog();
      })
      .catch((error: ApiError) =>
        match(getTelephonyCallApiErrorCode(error))
          .with(TELEPHONY_CALL_ERROR_CODES.USER_PHONE_MISSING, () =>
            setStartFailure('NO_USER_PHONE')
          )
          .with(TELEPHONY_CALL_ERROR_CODES.LINE_UNAVAILABLE, () => setStartFailure('NO_LINE'))
          // The line changed under the dialog: reload the choices so a retry picks a current one.
          .with(
            TELEPHONY_CALL_ERROR_CODES.LINE_NOT_FOUND,
            TELEPHONY_CALL_ERROR_CODES.LINE_HAS_NO_NUMBER,
            () => {
              setSelectedLineId(null);
              refetchCallOptions();
              handleApiError(error);
            }
          )
          .with(
            TELEPHONY_CALL_ERROR_CODES.PROVIDER_FAILED,
            TELEPHONY_CALL_ERROR_CODES.BROWSER_CALLING_NOT_PROVISIONED,
            () => setStartFailure('START_FAILED')
          )
          .with(TELEPHONY_CALL_ERROR_CODES.CALL_IN_PROGRESS, () => {
            showNotification.error(i18n.t('page.telephony.call.callInProgressTooltip'));
            closeCurrentDialog();
          })
          .otherwise(() => handleApiError(error))
      )
      .finally(() => setPendingChannel(null));
  };

  const handleCallViaMobile = () => {
    setStartFailure(null);
    setPendingChannel('mobile');
    startCallOn('mobile');
  };

  // Ask for the microphone first, so a denied prompt never creates a call record.
  const handleCallFromBrowser = () => {
    setStartFailure(null);
    setPendingChannel('browser');

    requestMicrophoneAccess().then((failure) => {
      if (!isMountedRef.current) {
        return;
      }

      if (isNotNil(failure)) {
        setStartFailure(failure);
        setPendingChannel(null);
        return;
      }

      startCallOn('browser');
    });
  };

  const handleCall = () =>
    channel === 'browser' ? handleCallFromBrowser() : handleCallViaMobile();

  const channelCards = [
    {
      channel: 'browser' as const,
      isShown: isBrowserAvailable,
      title: i18n.t('page.telephony.call.channelBrowser'),
      icon: 'hardware/desktop_mac' as const,
      description: i18n.t('page.telephony.call.channelBrowserDescription'),
    },
    {
      channel: 'mobile' as const,
      isShown: isMobileAvailable,
      title: i18n.t('page.telephony.call.channelMobile'),
      icon: 'communication/phone' as const,
      description: i18n.t('page.telephony.call.howItWorksNoLine', {
        userPhone: formatPhoneNumber(callOptions?.userPhoneNumber ?? ''),
      }),
    },
  ].filter((card) => card.isShown);

  return (
    <DataStatus
      isLoading={isCustomerLoading || areOptionsLoading || isBranchListLoading}
      isError={isCustomerError || areOptionsError}
      minHeight={40}
    >
      <VStack spacing={4}>
        <Show when={isEmpty(phoneOptions)}>
          <Alert
            variant="warning"
            message={i18n.t('page.telephony.call.customerHasNoPhone')}
            data-testid={suffixTestId('noCustomerPhone', props)}
          />
        </Show>

        <Show when={phoneOptions.length === 1}>
          <VStack spacing={1}>
            <Text size="xSmall" color="secondary">
              {i18n.t('page.telephony.call.customerPhoneNumber')}
            </Text>
            <Text size="base" alternative data-testid={suffixTestId('phoneNumber', props)}>
              {head(phoneOptions)?.label}
            </Text>
          </VStack>
        </Show>

        <Show when={phoneOptions.length > 1}>
          <Radio
            label={i18n.t('page.telephony.call.customerPhoneNumber')}
            options={phoneOptions}
            value={phoneNumber}
            onChange={setSelectedPhoneNumber}
            direction="column"
            isRequired
            isDisabled={isPending}
            data-testid={suffixTestId('phoneNumber', props)}
          />
        </Show>

        <Show when={!isEmpty(lines)}>
          <Choice<string>
            label={i18n.t('page.telephony.call.lineLabel')}
            placeholder={i18n.t('page.telephony.call.linePlaceholder')}
            options={lines.map((line) => ({value: line.id, label: getLineLabel(line, branchList)}))}
            value={lineId}
            onChange={setSelectedLineId}
            isRequired
            isNotClearable
            isDisabled={isPending}
            data-testid={suffixTestId('line', props)}
          />
        </Show>

        <Show when={isNil(branchLine) && !isEmpty(lines)}>
          <Alert
            variant="info"
            message={i18n.t('page.telephony.call.branchHasNoLine')}
            data-testid={suffixTestId('branchHasNoLine', props)}
          />
        </Show>

        <Show when={!isBlocked && !isEmpty(channelCards)}>
          <VStack spacing={1}>
            <Text size="xSmall" color="secondary">
              {i18n.t('page.telephony.call.callThrough')}
            </Text>
            <VStack spacing={2}>
              {channelCards.map((card) => (
                <Clickable
                  key={card.channel}
                  width="100%"
                  onClick={() => setChosenChannel(card.channel)}
                  isDisabled={isPending}
                  data-testid={suffixTestId(`channel-${card.channel}`, props)}
                >
                  <Card
                    variant="inlineWhite"
                    title={card.title}
                    titleIcon={card.icon}
                    control={{
                      type: 'radio',
                      name: CHANNEL_RADIO_NAME,
                      value: channel === card.channel,
                      onChange: () => setChosenChannel(card.channel),
                      isDisabled: isPending,
                    }}
                  >
                    <Text size="small" color="secondary">
                      {card.description}
                    </Text>
                  </Card>
                </Clickable>
              ))}
            </VStack>
          </VStack>
        </Show>

        {match(blocker)
          .with('NOT_ENABLED', () => (
            <Alert
              variant="warning"
              message={i18n.t('page.telephony.call.notEnabledTooltip')}
              data-testid={suffixTestId('notEnabled', props)}
            />
          ))
          .with('NO_USER_PHONE', () => (
            <Alert
              variant="warning"
              title={i18n.t('page.telephony.call.errorNoUserPhoneTitle')}
              message={i18n.t('page.telephony.call.errorNoUserPhone')}
              data-testid={suffixTestId('noUserPhone', props)}
            />
          ))
          .with('NO_LINE', () => (
            <Alert
              variant="warning"
              title={i18n.t('page.telephony.call.errorNoLineTitle')}
              message={i18n.t('page.telephony.call.errorNoLine')}
              hyperlinks={
                canWriteTelephonySettings
                  ? [
                      {
                        title: i18n.t('page.telephony.call.openTelephonySettings'),
                        onClick: handleOpenTelephonySettings,
                      },
                    ]
                  : undefined
              }
              data-testid={suffixTestId('noLine', props)}
            />
          ))
          .with('START_FAILED', () => (
            <Alert
              variant="error"
              title={i18n.t('page.telephony.call.errorStartFailedTitle')}
              message={i18n.t('page.telephony.call.errorStartFailed')}
              data-testid={suffixTestId('startFailed', props)}
            />
          ))
          .with('MICROPHONE_BLOCKED', () => (
            <Alert
              variant="error"
              message={i18n.t('page.telephony.browser.microphoneBlocked')}
              data-testid={suffixTestId('microphoneBlocked', props)}
            />
          ))
          .with('NO_MICROPHONE', () => (
            <Alert
              variant="error"
              message={i18n.t('page.telephony.browser.noMicrophone')}
              data-testid={suffixTestId('noMicrophone', props)}
            />
          ))
          .with(null, () => null)
          .exhaustive()}

        <Show when={!isBlocked && isNotNil(selectedLine)}>
          <Alert
            variant="info"
            message={i18n.t('page.telephony.call.customerSeesLine', {
              line: isNotNil(selectedLine) ? getLineLabel(selectedLine, branchList) : '',
            })}
            data-testid={suffixTestId('customerSeesLine', props)}
          />
        </Show>

        <ButtonGroup align="right">
          <Button
            variant="secondary"
            title={i18n.t('general.actions.cancel')}
            onClick={closeCurrentDialog}
            data-testid={suffixTestId('cancel', props)}
          />
          <Button
            variant="primary"
            leftIcon="communication/call"
            title={
              blocker === 'START_FAILED'
                ? i18n.t('general.actions.retry')
                : i18n.t('general.actions.call')
            }
            onClick={handleCall}
            isDisabled={!canSubmit}
            isLoading={isPending}
            data-testid={suffixTestId('submit', props)}
          />
        </ButtonGroup>
      </VStack>
    </DataStatus>
  );
}
